import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CadrageReadWithTeam, CadrageUpdate } from '../../../types';
import { statusToColor, statusToLabel } from '../../../utils/displayUtils';
import Input from '../../Forms/Input';
import Select from '../../Forms/Select';
import Textarea from '../../Forms/Textarea';
import MarkdownDisplay from '../../Common/MarkdownDisplay';
import CompletionIndicator from '../../Common/CompletionIndicator';

import { useTranslation } from 'react-i18next';

const isFilled = (s: string | null | undefined) => !!(s && s.trim().length > 0);
import cadrageService from '../../../services/cadrageService';
import notify from '@/lib/notify';

interface CadrageGeneralInfoProps {
  cadrage: CadrageReadWithTeam | null;
  onRefresh?: () => Promise<void>;
  isReadOnly?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
}

interface CadrageFormData {
  name: string;
  description: string | null;
  synthese: string | null;
  client: string;
  status: string;
  type: string;
}

const CadrageGeneralInfoSection: React.FC<CadrageGeneralInfoProps> = ({
  cadrage,
  onRefresh,
  isReadOnly = false,
  onDelete,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  
  const cadrageStatuses = [
    { value: 'draft', label: t('cadrage.general.statuses.draft', 'Brouillon') },
    { value: 'validated', label: t('cadrage.general.statuses.validated', 'Validé') },
    { value: 'planned', label: t('cadrage.general.statuses.planned', 'Planifié') },
    { value: 'progress', label: t('cadrage.general.statuses.progress', 'En cours') },
    { value: 'forge', label: t('cadrage.general.statuses.forge', 'En Forge') },
    { value: 'deployed', label: t('cadrage.general.statuses.deployed', 'Déployé') },
  ];
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const defaultFormValues: CadrageFormData = cadrage ? {
    name: cadrage.name || '',
    description: cadrage.description || null,
    synthese: cadrage.synthese || null,
    client: cadrage.client || '',
    status: cadrage.status || 'draft',
    type: cadrage.type || 'architecture',
  } : {
    name: '',
    description: null,
    synthese: null,
    client: '',
    status: 'draft',
    type: 'architecture',
  };

  const { register, reset, getValues, formState: { errors } } = useForm<CadrageFormData>({
    defaultValues: defaultFormValues
  });

  const handleSave = async () => {
    if (!cadrage?.id) return;
    setIsSaving(true);

    const values = getValues();
    const updateData: CadrageUpdate = {
      name: values.name.trim() !== '' ? values.name : cadrage.name,
      description: values.description || null,
      synthese: values.synthese || null,
      client: values.client.trim() !== '' ? values.client : cadrage.client,
      status: values.status as any,
      type: values.type as any,
    };

    try {
      await cadrageService.updateCadrage(cadrage.id, updateData);
      if (onRefresh) {
        await onRefresh();
      }
      setIsEditing(false);
      notify.success(t('common.success.save', 'Modifications sauvegardées avec succès.'));
    } catch (error) {
      notify.error(`${t('common.errors.save', 'Erreur lors de la sauvegarde:')} ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset(defaultFormValues);
  };

  useEffect(() => {
    if (cadrage) {
      reset({
        name: cadrage.name || '',
        description: cadrage.description || null,
        synthese: cadrage.synthese || null,
        client: cadrage.client || '',
        status: cadrage.status || 'draft',
        type: cadrage.type || 'architecture',
      });
    } else {
      reset({
        name: '',
        description: null,
        synthese: null,
        client: '',
        status: 'draft',
        type: 'architecture',
      });
    }
  }, [cadrage, reset, isEditing]);

  if (!cadrage && !isEditing) {
    return null;
  }

  const completionFields = cadrage
    ? [
        { label: t('cadrage.general.synthese', 'Synthèse'), filled: isFilled(cadrage.synthese) },
        { label: t('cadrage.general.description', 'Description'), filled: isFilled(cadrage.description) },
        { label: t('cadrage.general.client', 'Client'), filled: isFilled(cadrage.client) },
        { label: t('cadrage.general.contactName', 'Contact principal'), filled: isFilled(cadrage.main_contact_name) },
        { label: t('cadrage.general.contactEmail', 'Email du contact'), filled: isFilled(cadrage.main_contact_email) },
      ]
    : [];

  return (
    <div className="bg-card shadow rounded-lg p-6 space-y-4">
      {!isEditing && cadrage && (
        <CompletionIndicator fields={completionFields} className="mb-2" />
      )}
      <div className="flex justify-between items-center border-b border-border pb-2 mb-4">
        <h2 className="text-xl font-semibold text-foreground">
          {t('cadrage.general.title', 'Informations générales')}
        </h2>
        {!isReadOnly && (
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-xs font-medium rounded-md text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                >
                  {t('global.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                >
                  {isSaving ? t('global.saving') : t('global.save')}
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-xs font-medium rounded-md text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  {t('cadrage.detail.editButton')}
                </button>
                {onDelete && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={isDeleting}
                    aria-label={t('global.delete', 'Supprimer')}
                    title={t('global.delete', 'Supprimer')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <Input
            label={t('cadrage.general.fields.name', 'Nom du cadrage')}
            name="name"
            register={register}
            rules={{ required: t('cadrage.general.errors.nameRequired', 'Le nom est requis') }}
            error={errors.name}
          />
          <Textarea
            label={t('global.description', 'Description')}
            name="description"
            register={register}
            error={errors.description as any}
            rows={3}
          />
          <Textarea
            label={t('cadrage.general.fields.synthese', 'Synthèse')}
            name="synthese"
            register={register}
            error={errors.synthese as any}
            rows={10}
          />
          <Input
            label={t('global.client', 'Client')}
            name="client"
            register={register}
            rules={{ required: t('cadrage.general.errors.clientRequired', 'Le client est requis') }}
            error={errors.client}
          />
          <Select
            label={t('global.status')}
            name="status"
            register={register}
            options={cadrageStatuses}
            error={errors.status as any}
          />
        </div>
      ) : (
        <>
          {cadrage ? (
            <>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('global.name')}
                </p>
                <p className="text-lg text-foreground">{cadrage.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('global.description', 'Description')}
                </p>
                <div className="text-base text-foreground">
                  <MarkdownDisplay content={cadrage.description} emptyMessage={t('cadrage.general.fields.descriptionNotDefined', 'Non définie')} />
                </div>
              </div>
              {cadrage.synthese && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('cadrage.general.fields.synthese', 'Synthèse')}
                  </p>
                  <div className="text-base text-foreground">
                    <MarkdownDisplay content={cadrage.synthese} />
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('global.client', 'Client')}
                </p>
                <p className="text-lg text-foreground">{cadrage.client}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('global.status')}
                </p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusToColor(cadrage.status as any)}`}>
                  {statusToLabel(cadrage.status as any)}
                </span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic">{t('cadrage.general.loading', 'Chargement des informations...')}</p>
          )}
        </>
      )}
    </div>
  );
};

CadrageGeneralInfoSection.displayName = 'CadrageGeneralInfoSection';

export default CadrageGeneralInfoSection;
